/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useMemo, useState} from 'react'
import {BarChart} from '@/_metronic/partials/content/chart/BarChart'
import {RevenueStreamOutlet} from '../core/_models'
import {DashboardChartCardHeader, type DashboardChartMetric} from './DashboardChartCardHeader'

export type OutletMetric = DashboardChartMetric

type Props = {
  outletData?: RevenueStreamOutlet[] | null
  isLoading?: boolean
}

const METRIC_LABELS: Record<OutletMetric, string> = {
  revenue: 'Revenue',
  transaction: 'Transaction',
  item: 'Item',
  customer: 'Customer',
}

const SalesByOutletChart: FC<Props> = ({
  outletData = [],
  isLoading = false,
}) => {
  const [metric, setMetric] = useState<OutletMetric>('revenue')

  const categories = useMemo(
    () => outletData?.map((o) => o.outlet_name || '-') ?? [],
    [outletData]
  )

  const series = useMemo(() => {
    if (metric === 'revenue') {
      return [
        {
          name: 'Revenue',
          data:
            outletData?.map((o) => o.total_transaction_value ?? 0) ?? [],
        },
      ]
    }
    if (metric === 'transaction') {
      return [
        {
          name: 'Transaction',
          data: outletData?.map((o) => o.total_transaction ?? 0) ?? [],
        },
      ]
    }
    // item & customer - API doesn't provide per outlet; show zeros
    return [
      {
        name: METRIC_LABELS[metric],
        data: outletData?.map(() => 0) ?? [],
      },
    ]
  }, [metric, outletData])

  const total = useMemo(() => {
    if (metric === 'revenue') {
      return outletData?.reduce((s, o) => s + (o.total_transaction_value ?? 0), 0) ?? 0
    }
    if (metric === 'transaction') {
      return outletData?.reduce((s, o) => s + (o.total_transaction ?? 0), 0) ?? 0
    }
    return 0
  }, [metric, outletData])

  const isCurrency = metric === 'revenue'

  return (
    <div className='col-12 col-xl-6 mb-5 mb-xl-10'>
      <div className='card border-0 shadow-sm h-100'>
        <DashboardChartCardHeader
          subtitle='Outlet'
          metric={metric}
          onMetricChange={setMetric}
        />
        <div className='card-body'>
          <BarChart
            className='border-0 shadow-none bg-transparent'
            color={metric === 'revenue' ? 'primary' : 'success'}
            title=''
            series={series}
            categories={categories}
            total={total}
            isCurrency={isCurrency}
            isTotal={false}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  )
}

export {SalesByOutletChart}
